import { useMemo, useState } from "react";
import { SEO } from "@/components/SEO";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  ArrowRight,
  Award,
  CalendarIcon,
  CheckCircle2,
  GraduationCap,
  Infinity as InfinityIcon,
  Info,
  Rocket,
  Search,
  Sparkles,
  Star,
  Video,
  X,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePublicPrograms, useAllOpenCohorts, type Program, type PublicCohort } from "@/hooks/usePrograms";
import { RouteInterestButton } from "@/components/public/RouteInterestButton";
import { SelfPacedRouteCta } from "@/components/public/SelfPacedRouteCta";
import { useMySelfPacedEnrollments, useSelfPacedProgress } from "@/hooks/useSelfPacedRoutes";
import { useMyRouteStatus } from "@/hooks/useMyRouteStatus";
import { useSelectedCohort } from "@/hooks/useSelectedCohort";
import { useDiagnostic } from "@/hooks/useParticipantData";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { routeImage } from "@/lib/routeImage";

const levelLabels: Record<number, string> = {
  1: "Básico",
  2: "Intermedio",
  3: "Avanzado",
  4: "Experto",
};

const levelMeta = (level?: number | null) => (level ? levelLabels[level] || `Nivel ${level}` : null);

/* Mapea el nivel de madurez del diagnóstico (0-5) al nivel de ruta (1-4) */
const maturityToRouteLevel = (maturity?: number | null): number | null => {
  if (maturity == null) return null;
  if (maturity <= 1) return 1;
  if (maturity <= 3) return 2;
  if (maturity === 4) return 3;
  return 4;
};

/* ── Botón destacado: rutas recomendadas según diagnóstico ── */
const DiagnosticRecommendButton = ({
  diagnostic,
  recommendedLevel,
  onRecommend,
}: {
  diagnostic: any;
  recommendedLevel: number | null;
  onRecommend: () => void;
}) => {
  const navigate = useNavigate();

  if (!diagnostic) {
    return (
      <Button
        type="button"
        onClick={() => navigate('/diagnostic')}
        className="w-full sm:w-auto gap-2 bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-md shadow-primary/25 hover:opacity-90"
      >
        <Sparkles className="w-4 h-4" />
        Haz tu diagnóstico y te recomendamos rutas
      </Button>
    );
  }

  return (
    <Button
      type="button"
      onClick={onRecommend}
      className="w-full sm:w-auto gap-2 bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-md shadow-primary/25 hover:opacity-90"
    >
      <Sparkles className="w-4 h-4" />
      Rutas para mi nivel
      {recommendedLevel && (
        <span className="text-[10px] font-semibold bg-background/20 rounded-full px-2 py-0.5">
          {levelLabels[recommendedLevel]}
        </span>
      )}
    </Button>
  );
};

type StatusFilter = "all" | "not_started" | "in_progress" | "completed";

const statusChips: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "not_started", label: "No iniciadas" },
  { value: "in_progress", label: "En curso" },
  { value: "completed", label: "Completadas" },
];

/* ── Tarjeta de ruta con acompañamiento ── */
const GuidedCard = ({
  program,
  cohort,
  enrolledCohortId,
  hasCertificate,
  onClick,
}: {
  program: Program;
  cohort?: PublicCohort;
  enrolledCohortId?: string;
  hasCertificate?: boolean;
  onClick: () => void;
}) => {
  const navigate = useNavigate();
  const { setSelectedCohortId } = useSelectedCohort();

  const goToCalendar = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (enrolledCohortId) setSelectedCohortId(enrolledCohortId);
    navigate('/calendar');
  };

  return (
  <div
    className={cn(
      "group cursor-pointer rounded-xl border bg-card hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full",
      hasCertificate ? "border-success/40 hover:border-success/60" : "border-border/50 hover:border-primary/30",
    )}
    onClick={onClick}
  >
    <div className="relative aspect-video overflow-hidden bg-muted">
      <img
        src={routeImage(program)}
        alt={program.name}
        loading="lazy"
        width={1024}
        height={576}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      {hasCertificate && (
        <Badge className="absolute top-2 left-2 gap-1 text-[10px] font-normal bg-success text-success-foreground hover:bg-success">
          <CheckCircle2 className="w-3 h-3" /> Completada
        </Badge>
      )}
    </div>
    <div className="p-3.5 flex-1 flex flex-col gap-3">
      <h4 className="font-semibold text-foreground text-sm leading-tight line-clamp-2">{program.name}</h4>
      <p className="text-xs text-muted-foreground">
        {[levelMeta(program.level), program.duration].filter(Boolean).join(" · ")}
      </p>

      <div className="flex flex-wrap gap-1.5">
        <Badge variant="secondary" className="text-[10px] font-normal gap-1">
          <GraduationCap className="w-3 h-3" /> Certificada
        </Badge>
        <Badge variant="secondary" className="text-[10px] font-normal gap-1">
          <Video className="w-3 h-3" /> Sesiones en vivo
        </Badge>
        <Badge
          variant="secondary"
          className={cn(
            "text-[10px] font-normal gap-1",
            cohort?.start_date && "bg-success/10 text-success border-success/20"
          )}
        >
          <CalendarIcon className="w-3 h-3" />
          {cohort?.start_date
            ? `Inicia ${format(new Date(cohort.start_date), "MMM yyyy", { locale: es })}`
            : "Por programar"}
        </Badge>
      </div>

      <div className="mt-auto pt-1 flex flex-col gap-2">
        {hasCertificate ? (
          <Button size="sm" className="w-full" onClick={(e) => { e.stopPropagation(); navigate('/profile'); }}>
            <Award className="w-4 h-4 mr-1" /> Ver certificado
          </Button>
        ) : enrolledCohortId ? (
          <Button size="sm" className="w-full" onClick={goToCalendar}>
            <ArrowRight className="w-4 h-4 mr-1" /> Continuar
          </Button>
        ) : (
          <RouteInterestButton programId={program.id} programName={program.name} className="w-full" />
        )}
        <Button variant="outline" size="sm" className="w-full" onClick={(e) => { e.stopPropagation(); onClick(); }}>
          <Info className="w-4 h-4 mr-1" /> Ver info
        </Button>
      </div>

    </div>
  </div>
  );
};

/* ── Panel de rutas con acompañamiento ── */
const GuidedPanel = ({
  programs,
  cohortsByProgram,
  cohortByProgram,
  certByProgram,
  statusFilter,
  onStatusFilterChange,
}: {
  programs: Program[];
  cohortsByProgram: Record<string, PublicCohort>;
  cohortByProgram: Record<string, string>;
  certByProgram: Record<string, boolean>;
  statusFilter: StatusFilter;
  onStatusFilterChange: (v: StatusFilter) => void;
}) => {
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return programs;
    return programs.filter(p => {
      const enrolled = !!cohortByProgram[p.id];
      const completed = !!certByProgram[p.id];
      if (statusFilter === 'not_started') return !enrolled;
      if (statusFilter === 'in_progress') return enrolled && !completed;
      return completed;
    });
  }, [programs, statusFilter, cohortByProgram, certByProgram]);

  return (
    <section className="space-y-4">

      <div className="flex flex-wrap gap-2">
        {statusChips.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => onStatusFilterChange(chip.value)}
            className={cn(
              "px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors",
              statusFilter === chip.value
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10">
          No encontramos rutas con esos filtros.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <GuidedCard
              key={p.id}
              program={p}
              cohort={cohortsByProgram[p.id]}
              enrolledCohortId={cohortByProgram[p.id]}
              hasCertificate={!!certByProgram[p.id]}
              onClick={() => navigate(`/rutas/${p.slug}`)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/* ── Tarjeta de ruta de autoaprendizaje ── */
const SelfPacedCard = ({
  program,
  enrolled,
  progress,
  cohortId,
  hasCertificate,
  onClick,
}: {
  program: Program;
  enrolled: boolean;
  progress?: number;
  cohortId?: string;
  hasCertificate?: boolean;
  onClick: () => void;
}) => {
  const completed = hasCertificate || (enrolled && progress === 100);
  const featured = !!(program as any).is_featured;
  return (
  <div
    className={cn(
      "group cursor-pointer rounded-xl border bg-card hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col",
      completed ? "border-success/40 hover:border-success/60" : "border-border/50 hover:border-secondary/40",
      featured && !completed && "border-amber-400/60 ring-1 ring-amber-400/40 hover:border-amber-400",
    )}
    onClick={onClick}
  >
    <div className="relative aspect-video overflow-hidden bg-muted">
      <img
        src={routeImage(program)}
        alt={program.name}
        loading="lazy"
        width={1024}
        height={576}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      <Badge className="absolute top-2 left-2 gap-1 text-[10px] font-normal bg-background/90 text-foreground border border-border/60 hover:bg-background/90">
        <InfinityIcon className="w-3 h-3" /> Autoaprendizaje
      </Badge>
      {completed ? (
        <Badge className="absolute top-2 right-2 gap-1 text-[10px] font-normal bg-success text-success-foreground hover:bg-success">
          <CheckCircle2 className="w-3 h-3" /> Completada
        </Badge>
      ) : featured && (
        <Badge className="absolute top-2 right-2 gap-1 text-[10px] font-normal bg-amber-500 text-white hover:bg-amber-500">
          <Star className="w-3 h-3 fill-current" /> Destacada
        </Badge>
      )}
    </div>

    <div className="p-4 flex-1 flex flex-col gap-3">
      <h4 className="font-semibold text-foreground text-sm leading-tight line-clamp-2 flex items-start gap-1.5">
        {featured && completed && <Star className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500 fill-current" />}
        {program.name}
      </h4>
      <p className="text-xs text-muted-foreground">
        {[levelMeta(program.level), program.duration].filter(Boolean).join(" · ")}
      </p>


      <div className="flex flex-wrap gap-1.5">
        <Badge variant="secondary" className="text-[10px] font-normal gap-1">
          <Zap className="w-3 h-3" /> A tu ritmo
        </Badge>
        <Badge variant="secondary" className="text-[10px] font-normal gap-1">
          <CheckCircle2 className="w-3 h-3" /> Incluida en tu plan
        </Badge>
        {!enrolled && !completed && (
          <Badge variant="secondary" className="text-[10px] font-normal gap-1 bg-primary/10 text-primary border-primary/20">
            <Rocket className="w-3 h-3" /> Empieza hoy
          </Badge>
        )}
      </div>

      <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
        {program.description}
      </p>

      {enrolled && typeof progress === 'number' && (
        <div className="space-y-1">
          <Progress value={progress} className="h-1.5" />
          <p className={cn("text-[10px]", progress === 100 ? "text-success font-medium" : "text-muted-foreground")}>
            {progress === 100 ? 'Ruta completada' : `${progress}% completado`}
          </p>
        </div>
      )}

      <div className="mt-auto flex flex-col gap-2">
        <SelfPacedRouteCta
          programId={program.id}
          programName={program.name}
          description={program.description}
          enrolled={enrolled}
          progress={progress}
          cohortId={cohortId}
          hasCertificate={hasCertificate}
          className="w-full"
        />
        <Button variant="outline" size="sm" className="w-full" onClick={(e) => { e.stopPropagation(); onClick(); }}>
          <Info className="w-4 h-4 mr-1" /> Ver info
        </Button>
      </div>

    </div>
  </div>
  );
};


const PublicCertificationsPage = () => {
  const navigate = useNavigate();
  const { data: programs, isLoading } = usePublicPrograms();
  const { data: openCohorts } = useAllOpenCohorts();
  const { data: myRoutes } = useMySelfPacedEnrollments();
  const { data: routeStatus } = useMyRouteStatus();
  const { data: diagnostic } = useDiagnostic();
  const cohortByProgram = routeStatus?.cohortByProgram ?? {};
  const certByProgram = routeStatus?.certByProgram ?? {};

  const [activeTab, setActiveTab] = useState<'guided' | 'self-paced'>('self-paced');
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [guidedStatusFilter, setGuidedStatusFilter] = useState<StatusFilter>("all");
  const [recoActive, setRecoActive] = useState(false);

  const recommendedLevel = maturityToRouteLevel(diagnostic?.maturity_level);

  const applyRecommendation = () => {
    if (!recommendedLevel) return;
    setActiveTab('self-paced');
    setLevelFilter(String(recommendedLevel));
    setRecoActive(true);
  };

  const clearRecommendation = () => {
    setRecoActive(false);
    setLevelFilter("all");
  };

  const cohortsByProgram = (openCohorts || []).reduce((acc, c) => {
    if (!acc[c.program_id]) acc[c.program_id] = c;
    return acc;
  }, {} as Record<string, PublicCohort>);

  const allPrograms = programs || [];
  const guidedPrograms = allPrograms.filter(p => (p as any).modality !== 'self_paced');
  const selfPacedPrograms = allPrograms.filter(p => (p as any).modality === 'self_paced');

  const enrolledIds = myRoutes || [];
  const { data: progressMap } = useSelfPacedProgress(
    selfPacedPrograms.filter(p => enrolledIds.includes(p.id)).map(p => p.id),
  );

  const mainPrograms = guidedPrograms.filter(p => p.category !== "specialized");
  const specializedPrograms = guidedPrograms.filter(p => p.category === "specialized");
  const guidedOrdered = [...mainPrograms, ...specializedPrograms];

  const filteredGuided = useMemo(() => {
    if (guidedStatusFilter === 'all') return guidedOrdered;
    return guidedOrdered.filter(p => {
      const enrolled = !!cohortByProgram[p.id];
      const completed = !!certByProgram[p.id];
      if (guidedStatusFilter === 'not_started') return !enrolled;
      if (guidedStatusFilter === 'in_progress') return enrolled && !completed;
      return completed;
    });
  }, [guidedOrdered, guidedStatusFilter, cohortByProgram, certByProgram]);

  const filteredSelfPaced = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = selfPacedPrograms.filter(p => {
      if (q && !(`${p.name} ${p.description ?? ''}`.toLowerCase().includes(q))) return false;
      if (levelFilter !== 'all' && String(p.level) !== levelFilter) return false;
      if (categoryFilter !== 'all' && (p.category || 'general') !== categoryFilter) return false;
      if (statusFilter !== 'all') {
        const prog = progressMap?.[p.id] ?? 0;
        const enrolled = enrolledIds.includes(p.id);
        if (statusFilter === 'not_started' && enrolled) return false;
        if (statusFilter === 'in_progress' && !(enrolled && prog < 100)) return false;
        if (statusFilter === 'completed' && !(enrolled && prog >= 100)) return false;
      }
      if (featuredOnly && !(p as any).is_featured) return false;
      return true;
    });
    return [...list].sort((a, b) => {
      const fa = (a as any).is_featured ? 1 : 0;
      const fb = (b as any).is_featured ? 1 : 0;
      if (fa !== fb) return fb - fa;
      if (sortBy === 'level') return (a.level || 0) - (b.level || 0);
      if (sortBy === 'progress') return (progressMap?.[b.id] ?? -1) - (progressMap?.[a.id] ?? -1);
      return a.name.localeCompare(b.name);
    });
  }, [selfPacedPrograms, search, levelFilter, categoryFilter, sortBy, statusFilter, featuredOnly, progressMap, enrolledIds]);

  if (isLoading) {
    return (
      <div className="space-y-6 p-6 max-w-4xl mx-auto">
        <div className="text-center space-y-4">
          <Skeleton className="h-16 w-16 rounded-full mx-auto" />
          <Skeleton className="h-10 w-80 mx-auto" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
    );
  }

  const tabDescription = activeTab === 'guided'
    ? "Rutas con acompañamiento: sesiones en vivo, mentoría y certificación al completar."
    : "Rutas de autoaprendizaje: avanza a tu ritmo, cuando quieras, incluidas en tu plan.";

  return (
    <div className="space-y-8 p-6">
      <SEO
        title="Rutas de Aprendizaje en IA — LÍDER IA"
        description="Rutas de aprendizaje en IA con acompañamiento y rutas de autoaprendizaje a tu ritmo, para profesionales de todos los niveles."
        path="/rutas"
      />
      {/* Header */}
      <div className="text-center space-y-4 max-w-3xl mx-auto">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-2">
          <GraduationCap className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground">
          Rutas de Aprendizaje
        </h1>
        <p className="text-lg text-muted-foreground">
          Explora rutas de IA diseñadas para impulsar tu carrera.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center">
        <div className="inline-flex flex-wrap justify-center gap-2 p-1.5 rounded-2xl bg-muted/40 border border-border/60">
          <button
            type="button"
            onClick={() => setActiveTab('self-paced')}
            className={cn(
              "flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors",
              activeTab === 'self-paced'
                ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <InfinityIcon className="w-4 h-4" />
            Rutas de Autoaprendizaje
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('guided')}
            className={cn(
              "flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors",
              activeTab === 'guided'
                ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <GraduationCap className="w-4 h-4" />
            Rutas con Acompañamiento
          </button>

        </div>
      </div>

      {/* Descripción según tab */}
      <p className="text-center text-base text-muted-foreground max-w-2xl mx-auto">
        {tabDescription}
      </p>

      <div className="max-w-6xl mx-auto space-y-10">
        {activeTab === 'guided' ? (
          guidedPrograms.length > 0 ? (
            <>
              <GuidedPanel
                programs={filteredGuided}
                cohortsByProgram={cohortsByProgram}
                cohortByProgram={cohortByProgram}
                certByProgram={certByProgram}
                statusFilter={guidedStatusFilter}
                onStatusFilterChange={setGuidedStatusFilter}
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-10">
              No hay rutas con acompañamiento disponibles.
            </p>
          )
        ) : (
          selfPacedPrograms.length > 0 && (
            <section id="self-paced" className="scroll-mt-20">

              {/* Filtros */}
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar ruta..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={levelFilter} onValueChange={(v) => { setLevelFilter(v); setRecoActive(false); }}>
                  <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Nivel" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los niveles</SelectItem>
                    {Object.entries(levelLabels).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="Categoría" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    <SelectItem value="general">Principal</SelectItem>
                    <SelectItem value="specialized">Especializada</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Ordenar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Nombre</SelectItem>
                    <SelectItem value="level">Nivel</SelectItem>
                    <SelectItem value="progress">Mi progreso</SelectItem>
                  </SelectContent>
                </Select>
                <DiagnosticRecommendButton
                  diagnostic={diagnostic}
                  recommendedLevel={recommendedLevel}
                  onRecommend={applyRecommendation}
                />
              </div>

              {/* Aviso de recomendación activa */}
              {recoActive && recommendedLevel && (
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-primary/10 text-primary border border-primary/25 rounded-full px-3 py-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    Mostrando rutas recomendadas para tu nivel ({levelLabels[recommendedLevel]})
                    <button
                      type="button"
                      onClick={clearRecommendation}
                      aria-label="Quitar recomendación"
                      className="ml-1 rounded-full hover:bg-primary/15 p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              )}

              {/* Chips de estado */}
              <div className="flex flex-wrap gap-2 mb-4">
                {statusChips.map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => setStatusFilter(chip.value)}
                    className={cn(
                      "px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors",
                      statusFilter === chip.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setFeaturedOnly(v => !v)}
                  className={cn(
                    "px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1.5",
                    featuredOnly
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
                  )}
                >
                  <Star className={cn("w-3.5 h-3.5", featuredOnly && "fill-current")} /> Solo destacadas
                </button>
              </div>


              {filteredSelfPaced.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">
                  No encontramos rutas con esos filtros.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredSelfPaced.map((p) => (
                    <SelfPacedCard
                      key={p.id}
                      program={p}
                      enrolled={enrolledIds.includes(p.id)}
                      progress={progressMap?.[p.id]}
                      cohortId={cohortByProgram[p.id]}
                      hasCertificate={!!certByProgram[p.id]}
                      onClick={() => navigate(`/rutas/${p.slug}`)}
                    />
                  ))}
                </div>
              )}
            </section>
          )
        )}
      </div>
    </div>
  );
};

export default PublicCertificationsPage;
